if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');
        this.form = this.querySelector('.sticky-atc-bar__form');
        if (!this.container) return;
        if (this.form) this.form.dataset.productSection = this.dataset.section;

        this.variantData = this.getVariantData();
        this.select = this.querySelector('select[name="id"]');
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.select?.value)) || null;

        this.init();
        this.select?.addEventListener('change', this.handleVariantSelectChange.bind(this));
        this.form?.addEventListener('submit', this.syncFormBeforeSubmit.bind(this), { capture: true });
      }

      handleVariantSelectChange() {
        if (this.isUpdating) return;
        this.isUpdating = true;

        this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
        const selectedVariantId = this.querySelector(this.selectors.variantIdSelect)?.value;
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId)) || null;

        if (!this.currentVariant) {
          this.updateButton(true, FoxTheme.variantStrings.unavailable);
          this.isUpdating = false;
          return;
        }

        if (this.mainVariantSelects) {
          Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
            const variantOptionVal = this.currentVariant.options[index];
            if (!variantOptionVal) return;

            switch (element.tagName) {
              case 'SELECT':
                element.value = variantOptionVal;
                const options = element.querySelectorAll('option');
                options.forEach((option) => option.removeAttribute('selected'));

                element.value = variantOptionVal;
                const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                if (selectedOption) {
                  selectedOption.setAttribute('selected', 'selected');
                }
                break;
              case 'FIELDSET':
                Array.from(element.querySelectorAll('input')).forEach((radio) => {
                  if (radio.value === variantOptionVal) {
                    radio.checked = true;
                  }
                });
                break;
            }
          });
          this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
          this.isUpdating = false;
        } else {
          this.isUpdating = false;
        }

        this.updatePrice();
        this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
      }

      syncFormBeforeSubmit() {
        if (!this.form) return;

        const mainForm = this.productFormActions || document.querySelector('.main-product-form');
        this.syncFieldFromMainForm(mainForm, 'selling_plan', { removeWhenEmpty: true });
        this.syncQuantityFromMainForm(mainForm);
        this.syncLineItemPropertiesFromMainForm(mainForm);
      }

      syncQuantityFromMainForm(mainForm) {
        if (!mainForm) return;

        const stickyQuantity = this.form.querySelector('[name="quantity"]');
        const hasEditableStickyQuantity = stickyQuantity && stickyQuantity.type !== 'hidden';
        if (hasEditableStickyQuantity) return;

        this.syncFieldFromMainForm(mainForm, 'quantity', { removeWhenEmpty: true });
      }

      syncLineItemPropertiesFromMainForm(mainForm) {
        if (!mainForm) return;

        const mainProperties = Array.from(mainForm.querySelectorAll('[name^="properties["]'));
        const mainPropertyNames = new Set(mainProperties.map((input) => input.name));

        Array.from(this.form.querySelectorAll('[name^="properties["]')).forEach((input) => {
          if (!mainPropertyNames.has(input.name)) input.remove();
        });

        mainProperties.forEach((input) => {
          this.setHiddenFormField(input.name, input.value);
        });
      }

      syncFieldFromMainForm(mainForm, fieldName, options = {}) {
        if (!mainForm) return;

        const source = mainForm.querySelector(`[name="${fieldName}"]`);
        this.setHiddenFormField(fieldName, source?.value || '', options);
      }

      setHiddenFormField(fieldName, value, { removeWhenEmpty = false } = {}) {
        const inputs = Array.from(this.form.querySelectorAll('input')).filter((input) => input.name === fieldName);
        const [input, ...duplicates] = inputs;
        duplicates.forEach((duplicate) => duplicate.remove());

        if (removeWhenEmpty && !value) {
          input?.remove();
          return;
        }

        const targetInput = input || document.createElement('input');
        targetInput.type = 'hidden';
        targetInput.name = fieldName;
        targetInput.value = value;

        if (!input) {
          this.form.appendChild(targetInput);
        }
      }

      getVariantData() {
        if (this.variantData) return this.variantData;

        const variantDataElement = this.mainProductInfo?.querySelector(':scope > [data-product-variants]');
        if (!variantDataElement) return [];

        try {
          const variants = JSON.parse(variantDataElement.textContent);
          this.variantData = Array.isArray(variants) ? variants : [];
        } catch (error) {
          console.error(error);
          this.variantData = [];
        }
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }
        this.productId = this.dataset.productId;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.form || this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('.btn__text');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (!addButtonText || !this.currentVariant) return;
          const price = new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(this.currentVariant.price/100).replace(/\s/g, '');
          addButtonText.innerHTML = FoxTheme.getProductSubmitContent(addButton, price);
        }
      }

      updatePrice() {
        if (!this.currentVariant) return;

        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        if (!priceWrapper) return;

        const unitPrice = unitPriceWrapper?.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unitPriceWrapper && unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else if (unitPriceWrapper) {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      _getBaseUnit() {
        const unitPriceMeasurement = this.currentVariant?.unit_price_measurement;
        if (!unitPriceMeasurement) return '';

        const referenceValue = unitPriceMeasurement.reference_value;
        const referenceUnit = unitPriceMeasurement.reference_unit;
        return referenceValue === 1 ? referenceUnit : `${referenceValue}${referenceUnit}`;
      }

      syncWithMainProductForm() {
        if (!window.FoxTheme?.pubsub || !this.mainProductInfo) return;

        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          if (!variant || !variantInput) {
            this.currentVariant = null;
            this.updateButton(true, FoxTheme.variantStrings.unavailable);
            return;
          }

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updatePrice();
          this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
        });
      }
    }
  );
}
